import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import clsx from 'clsx';

const SECTIONS = [
  { key: 'customer_snapshot',      label: 'Customer Snapshot',      icon: '🏢' },
  { key: 'competitive_landscape',  label: 'Competitive Landscape',  icon: '⚔️'  },
  { key: 'industry_trends',        label: 'Industry Trends',        icon: '📈' },
  { key: 'opportunities',          label: 'Opportunities',          icon: '🎯' },
  { key: 'risks',                  label: 'Risks',                  icon: '⚠️'  },
  { key: 'talking_points',         label: 'Talking Points',         icon: '💬' },
];

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function ResearchBrief({ customerId }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [activeBriefId, setActiveBriefId] = useState(null);
  const [showSources, setShowSources] = useState(false);
  const [repNotes, setRepNotes] = useState('');
  const [notesModified, setNotesModified] = useState(false);
  const notesTimerRef = useRef(null);

  // List of briefs for this customer
  const listQ = useQuery({
    queryKey: ['research-list', customerId],
    queryFn: () => api.get(`/research/customers/${customerId}`).then(r => r.data.data),
    refetchInterval: (q) => {
      const rows = q.state.data || [];
      return rows.some(b => b.status === 'generating') ? 4000 : false;
    },
  });

  const briefs = listQ.data || [];
  const latest = briefs[0] || null;
  const isGenerating = latest?.status === 'generating';
  const isReady = latest?.status === 'ready';
  const hasError = latest?.status === 'error';

  // Auto-expand when a new ready brief arrives
  const prevStatusRef = useRef(null);
  useEffect(() => {
    if (prevStatusRef.current === 'generating' && latest?.status === 'ready') {
      setExpanded(true);
      setActiveBriefId(latest.id);
    }
    prevStatusRef.current = latest?.status;
  }, [latest?.status]);

  // Full brief detail
  const briefId = activeBriefId || (isReady ? latest?.id : null);
  const detailQ = useQuery({
    queryKey: ['research-brief', briefId],
    queryFn: () => api.get(`/research/briefs/${briefId}`).then(r => r.data.data),
    enabled: !!briefId,
  });

  const brief = detailQ.data;

  // Sync rep notes from loaded brief
  useEffect(() => {
    if (brief && !notesModified) {
      setRepNotes(brief.rep_notes || '');
    }
  }, [brief?.id]);

  // Sources
  const sourcesQ = useQuery({
    queryKey: ['research-sources', briefId],
    queryFn: () => api.get(`/research/briefs/${briefId}/sources`).then(r => r.data.data),
    enabled: !!briefId && showSources,
  });

  // Trigger new brief
  const generate = useMutation({
    mutationFn: () => api.post(`/research/customers/${customerId}`).then(r => r.data.data),
    onSuccess: () => {
      setExpanded(true);
      setShowSources(false);
      setNotesModified(false);
      qc.invalidateQueries({ queryKey: ['research-list', customerId] });
    },
  });

  // Save rep notes (debounced auto-save)
  const saveNotes = useMutation({
    mutationFn: (notes) => api.put(`/research/briefs/${briefId}/notes`, { rep_notes: notes }),
    onSuccess: () => {
      setNotesModified(false);
      qc.invalidateQueries({ queryKey: ['research-brief', briefId] });
    },
  });

  function handleNotesChange(val) {
    setRepNotes(val);
    setNotesModified(true);
    clearTimeout(notesTimerRef.current);
    notesTimerRef.current = setTimeout(() => saveNotes.mutate(val), 1500);
  }

  // Collapsed header row
  return (
    <section className="card p-4">
      <div className="flex items-center justify-between">
        <button
          className="flex items-center gap-2 group"
          onClick={() => {
            if (!expanded && isReady) setActiveBriefId(latest.id);
            setExpanded(v => !v);
          }}
        >
          <span className="section-eyebrow group-hover:text-navy">Research Brief</span>
          {isGenerating && <span className="text-xs text-amber-600 animate-pulse">Generating…</span>}
          {isReady && !expanded && <span className="text-xs text-green-700">Ready ✓</span>}
          {hasError && <span className="text-xs text-crimson">Error</span>}
          <span className="text-muted text-xs">{expanded ? '▲' : '▼'}</span>
        </button>

        <button
          onClick={() => generate.mutate()}
          disabled={generate.isPending || isGenerating}
          className={clsx(
            'text-xs px-3 py-1 rounded border transition',
            isGenerating || generate.isPending
              ? 'border-muted text-muted cursor-not-allowed'
              : isReady
              ? 'border-navy/30 text-navy hover:bg-panel'
              : 'border-navy text-navy font-medium hover:bg-navy hover:text-white'
          )}
        >
          {isGenerating || generate.isPending ? 'Generating…' : isReady ? '↻ Refresh' : '✦ Run Research'}
        </button>
      </div>

      <div className="rule-crimson" />

      {/* Collapsed empty state */}
      {!expanded && !isReady && !isGenerating && (
        <p className="text-sm text-muted mt-2">
          {hasError
            ? 'Last research run failed. Click Refresh to try again.'
            : 'No brief yet. Click "Run Research" to analyze this customer before your next visit.'}
        </p>
      )}

      {/* Generating spinner */}
      {isGenerating && !expanded && (
        <div className="mt-3 flex items-center gap-2 text-sm text-muted">
          <span className="animate-spin">⟳</span>
          <span>Searching web + synthesizing intelligence… est. 30–60 seconds</span>
        </div>
      )}

      {/* Expanded content */}
      {expanded && (
        <div className="mt-3 space-y-4">
          {/* Generating state while expanded */}
          {isGenerating && (
            <div className="flex flex-col items-center py-8 gap-3 text-muted">
              <span className="text-3xl animate-spin">⟳</span>
              <div className="text-sm">Searching web + synthesizing intelligence…</div>
              <div className="text-xs text-muted/60">est. 30–60 seconds</div>
            </div>
          )}

          {/* Error state */}
          {hasError && !brief && (
            <div className="text-crimson text-sm py-4 text-center">
              Research failed. Check server logs or try refreshing.
            </div>
          )}

          {/* Brief content */}
          {brief && brief.status !== 'generating' && (
            <>
              {/* Meta row */}
              <div className="flex items-center gap-3 text-xs text-muted flex-wrap">
                <span>Generated {fmtDate(brief.ready_at || brief.created_at)}</span>
                {brief.accuracy_score != null && (
                  <span className={clsx(
                    'px-1.5 py-0.5 rounded font-medium',
                    brief.accuracy_score >= 0.8 ? 'bg-green-50 text-green-700' :
                    brief.accuracy_score >= 0.6 ? 'bg-amber-50 text-amber-700' :
                    'bg-red-50 text-red-700'
                  )}>
                    {Math.round(brief.accuracy_score * 100)}% accuracy
                  </span>
                )}
                {brief.requested_by_name && <span>by {brief.requested_by_name}</span>}
                {brief.flagged && (
                  <span className="bg-crimson/10 text-crimson px-1.5 py-0.5 rounded">⚑ Flagged for review</span>
                )}
              </div>

              {/* Six sections grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {SECTIONS.map(s => (
                  <BriefSection
                    key={s.key}
                    icon={s.icon}
                    label={s.label}
                    content={brief[s.key]}
                  />
                ))}
              </div>

              {/* Sources toggle */}
              <div>
                <button
                  onClick={() => setShowSources(v => !v)}
                  className="text-xs text-navy/60 hover:text-navy underline"
                >
                  {showSources ? 'Hide sources' : 'View sources'}
                </button>
                {showSources && (
                  <SourcesList
                    sources={sourcesQ.data}
                    loading={sourcesQ.isLoading}
                  />
                )}
              </div>

              {/* Rep notes */}
              <div>
                <div className="text-xs font-medium text-muted uppercase tracking-wide mb-1.5">
                  My prep notes
                </div>
                <textarea
                  value={repNotes}
                  onChange={e => handleNotesChange(e.target.value)}
                  placeholder="Add your own talking points, questions to ask, things to remember…"
                  className="input w-full text-sm min-h-[80px] resize-y"
                />
                {notesModified && (
                  <div className="text-[10px] text-muted mt-0.5">
                    {saveNotes.isPending ? 'Saving…' : 'Auto-saves in a moment'}
                  </div>
                )}
                {saveNotes.isError && (
                  <div className="text-crimson text-xs mt-0.5">Failed to save notes.</div>
                )}
              </div>

              {/* History */}
              {briefs.filter(b => b.id !== brief.id && b.status === 'ready').length > 0 && (
                <BriefHistory
                  briefs={briefs}
                  activeBriefId={brief.id}
                  onSelect={id => { setActiveBriefId(id); setNotesModified(false); setShowSources(false); }}
                />
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

function BriefSection({ icon, label, content }) {
  const [collapsed, setCollapsed] = useState(false);

  if (!content) {
    return (
      <div className="bg-panel rounded-lg p-3 border border-transparent opacity-50">
        <div className="text-xs font-medium text-muted uppercase tracking-wide">
          {icon} {label}
        </div>
        <div className="text-sm text-muted mt-1 italic">Not available</div>
      </div>
    );
  }

  const lines = content.split('\n').filter(l => l.trim());

  return (
    <div className="bg-panel rounded-lg p-3">
      <button
        onClick={() => setCollapsed(v => !v)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="text-xs font-semibold text-navy uppercase tracking-wide">
          {icon} {label}
        </div>
        <span className="text-muted text-xs">{collapsed ? '▼' : '▲'}</span>
      </button>
      {!collapsed && (
        <div className="mt-2 text-sm text-navy/90 leading-relaxed space-y-1">
          {lines.map((line, i) => (
            <p key={i} className={clsx(
              line.startsWith('•') || line.startsWith('-') || line.startsWith('*')
                ? 'pl-2'
                : ''
            )}>
              {line}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function SourcesList({ sources, loading }) {
  if (loading) return <div className="text-xs text-muted mt-2">Loading sources…</div>;
  if (!sources?.length) return <div className="text-xs text-muted mt-2">No sources recorded.</div>;

  // Group by query
  const byQuery = sources.reduce((acc, s) => {
    if (!acc[s.query]) acc[s.query] = [];
    acc[s.query].push(s);
    return acc;
  }, {});

  return (
    <div className="mt-2 space-y-3 border-l-2 border-panel pl-3">
      {Object.entries(byQuery).map(([query, results]) => (
        <div key={query}>
          <div className="text-[10px] uppercase tracking-wide text-muted font-medium mb-1">
            Search: "{query}"
          </div>
          <div className="space-y-1">
            {results.map((s, i) => (
              <a
                key={i}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-xs text-navy/70 hover:text-navy hover:underline truncate"
                title={s.title}
              >
                {s.title || s.url}
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function BriefHistory({ briefs, activeBriefId, onSelect }) {
  const [open, setOpen] = useState(false);
  const past = briefs.filter(b => b.id !== activeBriefId && b.status === 'ready');
  if (!past.length) return null;

  return (
    <div>
      <button onClick={() => setOpen(v => !v)} className="text-xs text-muted hover:text-navy">
        {open ? '▲ Hide' : '▼ Show'} {past.length} previous brief{past.length > 1 ? 's' : ''}
      </button>
      {open && (
        <div className="mt-2 space-y-1">
          {past.map(b => (
            <button
              key={b.id}
              onClick={() => onSelect(b.id)}
              className="block w-full text-left text-xs bg-panel hover:bg-panel/60 rounded px-3 py-2"
            >
              <span className="text-navy">{fmtDate(b.ready_at || b.created_at)}</span>
              {b.requested_by_name && <span className="text-muted ml-2">by {b.requested_by_name}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
